/**
 * AppContext — typed React context for global app state.
 *
 * Provides a single source of truth for values that were previously
 * scattered across sessionStorage (currency, country, auth state, etc.).
 *
 * Usage in components:
 *   import { useAppContext } from '../shared/app/provider/AppContext';
 *   const [ctx, setCtx] = useAppContext();
 */
import React, { createContext, useState, type ReactNode } from 'react';

import type { AppContextType, AppContextValue, PingUserInfo } from '../../shared/types';
import { STORAGE_KEYS } from '../../shared/utils/storage';

// ── Defaults ────────────────────────────────────────────────────────

const DEFAULT_CONTEXT: AppContextType = {
  currency: 'USD',
  country: 'US',
  channelId: null,
  channelName: null,
  storeKey: null,
  storeName: null,
  customerGroupId: null,
  customerGroupName: null,
  locale: 'en',
  loggedIn: false,
  authType: null,
  accessToken: null,
  userInfo: null,
  cart: null,
};

// ── Context ─────────────────────────────────────────────────────────

const AppContext = createContext<AppContextValue | undefined>(undefined);
AppContext.displayName = 'AppContext';

// ── Hydrate from sessionStorage (once, on mount) ────────────────────

function hydrateFromStorage(): AppContextType {
  // Wrapped in try/catch so this works in JSDOM (Jest) and Storybook
  // environments where sessionStorage may throw or be unavailable.
  try {
    const storedCurrency = sessionStorage.getItem(STORAGE_KEYS.CURRENCY);
    const storedCountry = sessionStorage.getItem(STORAGE_KEYS.COUNTRY);
    const pingToken =
      sessionStorage.getItem(STORAGE_KEYS.PING_ACCESS_TOKEN) ||
      localStorage.getItem(STORAGE_KEYS.PING_ACCESS_TOKEN);
    const pingUserStr =
      sessionStorage.getItem(STORAGE_KEYS.PING_USER_INFO) ||
      localStorage.getItem(STORAGE_KEYS.PING_USER_INFO);

    let userInfo: PingUserInfo | null = null;
    if (pingUserStr) {
      try {
        userInfo = JSON.parse(pingUserStr);
      } catch {
        userInfo = null;
      }
    }

    const ctx: AppContextType = {
      currency: storedCurrency || DEFAULT_CONTEXT.currency,
      country: storedCountry || DEFAULT_CONTEXT.country,
      channelId: sessionStorage.getItem(STORAGE_KEYS.CHANNEL_ID),
      channelName: sessionStorage.getItem(STORAGE_KEYS.CHANNEL_NAME),
      storeKey: sessionStorage.getItem(STORAGE_KEYS.STORE_KEY),
      storeName: sessionStorage.getItem(STORAGE_KEYS.STORE_NAME),
      customerGroupId: sessionStorage.getItem(STORAGE_KEYS.CUSTOMER_GROUP_ID),
      customerGroupName: sessionStorage.getItem(STORAGE_KEYS.CUSTOMER_GROUP_NAME),
      locale: sessionStorage.getItem(STORAGE_KEYS.LOCALE) || DEFAULT_CONTEXT.locale,
      loggedIn: !!pingToken,
      authType: pingToken ? 'ping' : null,
      accessToken: pingToken || null,
      userInfo,
      cart: null,
    };

    // Persist defaults so sessionStorage is consistent on first visit
    if (!storedCurrency) sessionStorage.setItem(STORAGE_KEYS.CURRENCY, ctx.currency);
    if (!storedCountry) sessionStorage.setItem(STORAGE_KEYS.COUNTRY, ctx.country);

    return ctx;
  } catch {
    // sessionStorage unavailable (private browsing restrictions, JSDOM, etc.)
    // Fall back to pure defaults — the app will still function correctly.
    return { ...DEFAULT_CONTEXT };
  }
}

// ── Provider ────────────────────────────────────────────────────────

interface AppProviderProps {
  children: ReactNode;
  /** Override initial state (useful for tests / Storybook) */
  initialState?: Partial<AppContextType>;
}

export function AppProvider({ children, initialState }: AppProviderProps) {
  const [state, setState] = useState<AppContextType>(() => ({
    ...hydrateFromStorage(),
    ...initialState,
  }));

  return (
    <AppContext.Provider value={[state, setState]}>
      {children}
    </AppContext.Provider>
  );
}

// useAppContext is exported from ./useAppContext to satisfy react-refresh/only-export-components

// Default export for backward-compat with `import AppContext from './appContext'`
export default AppContext;
